import React, {useEffect, useMemo, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select from "@amzn/meridian/select";
import SelectOption from "@amzn/meridian/select/select-option";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Icon from "@amzn/meridian/icon";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import plusTokens from "@amzn/meridian-tokens/base/icon/plus";
import get from "lodash/get";
import {
    SCHEDULE_MODAL_MODE,
    SORT_PATH_TYPE,
    SORT_PATH_SOURCE,
    resolvePairedPaths,
    eligiblePathsForType,
    validateScheduleForm,
    hasDateConflict,
    buildValidPathIdSet,
    toDateInputValue
} from "../../helper/PathScheduleHelper";

const DAYS_OF_WEEK = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

/**
 * Modal for creating/editing a path schedule entry (Workstream 2).
 *
 * Supports four modes (SCHEDULE_MODAL_MODE): CREATE, EDIT_ACTIVE, EDIT_STAGED, ACTIVATE_NOW.
 * Field editability and the effective-date rule vary by mode per the design doc:
 *  - CREATE / EDIT_STAGED: effective date editable and must be future.
 *  - EDIT_ACTIVE: effective date read-only (retains the active entry's date).
 *  - ACTIVATE_NOW: effective date is the station-local now (read-only), pre-filled from the staged entry.
 *
 * All network data (sortPathInfoList, sortPathSettings) and the initial form state are passed
 * in as props; the modal is presentation + local form state + validation only. Saving is
 * delegated to the onSave callback (which performs the SetPathSchedule call and refresh).
 */
const AddScheduleModal = (props) => {
    const {
        intl, open, mode, cycleId, stationNow,
        sortPathInfoList, sortPathSettings,
        initialForm, editingEffectiveDate,
        activeSchedule, stagedSchedules,
        isSaving, saveError,
        onClose, onSave
    } = props;

    const [effectiveDate, setEffectiveDate] = useState(undefined);
    const [selectedSettingId, setSelectedSettingId] = useState(undefined);
    const [fillPathId, setFillPathId] = useState(undefined);
    const [pickPathId, setPickPathId] = useState(undefined);
    const [overrides, setOverrides] = useState([]); // [{day, fillPathId, pickPathId}]
    const [validationErrors, setValidationErrors] = useState([]);
    const [conflictAcknowledged, setConflictAcknowledged] = useState(false);

    const validPathIds = useMemo(() => buildValidPathIdSet(sortPathInfoList), [sortPathInfoList]);
    const fillOptions = useMemo(
        () => eligiblePathsForType(SORT_PATH_TYPE.FILL, cycleId, sortPathInfoList),
        [cycleId, sortPathInfoList]);
    const pickOptions = useMemo(
        () => eligiblePathsForType(SORT_PATH_TYPE.PICK, cycleId, sortPathInfoList),
        [cycleId, sortPathInfoList]);

    const effectiveDateReadOnly = mode === SCHEDULE_MODAL_MODE.EDIT_ACTIVE
        || mode === SCHEDULE_MODAL_MODE.ACTIVATE_NOW;

    // Initialize form state whenever the modal opens or its mode/initial data changes.
    useEffect(() => {
        if (!open) {
            return;
        }
        if (mode === SCHEDULE_MODAL_MODE.ACTIVATE_NOW) {
            setEffectiveDate(toDateInputValue(stationNow) || undefined);
        } else {
            setEffectiveDate(toDateInputValue(get(initialForm, ["effectiveDate"])) || undefined);
        }
        setFillPathId(get(initialForm, ["fillPathId"]));
        setPickPathId(get(initialForm, ["pickPathId"]));
        setSelectedSettingId(undefined);
        const initialOverrides = get(initialForm, ["dayOfWeekOverrides"]) || {};
        setOverrides(Object.keys(initialOverrides).map((day) => ({
            day,
            fillPathId: get(initialOverrides, [day, "fillPathId"]),
            pickPathId: get(initialOverrides, [day, "pickPathId"])
        })));
        setValidationErrors([]);
        setConflictAcknowledged(false);
    }, [open, mode, initialForm, stationNow]);

    // Selecting a named configuration pre-fills its paired Fill/Pick path ids.
    const onSelectConfig = (settingId) => {
        setSelectedSettingId(settingId);
        if (settingId) {
            const paired = resolvePairedPaths(settingId, cycleId, sortPathInfoList);
            if (paired.fillPathId) {
                setFillPathId(paired.fillPathId);
            }
            if (paired.pickPathId) {
                setPickPathId(paired.pickPathId);
            }
        }
    };

    const overridesAsMap = () => {
        const map = {};
        overrides.forEach((o) => {
            if (o.day) {
                map[o.day] = { fillPathId: o.fillPathId, pickPathId: o.pickPathId };
            }
        });
        return map;
    };

    const currentForm = () => ({
        effectiveDate,
        fillPathId,
        pickPathId,
        dayOfWeekOverrides: overridesAsMap()
    });

    const conflictDetected = !effectiveDateReadOnly
        && hasDateConflict(effectiveDate, activeSchedule, stagedSchedules, editingEffectiveDate);

    // EDIT_STAGED reschedule: changing the effective date writes a NEW entry at the new date
    // and leaves the original staged entry in place (per HLD; the UI does not auto Set+Delete).
    // Warn the operator to delete the superseded original entry after confirming the new one.
    const isRescheduledStaged = mode === SCHEDULE_MODAL_MODE.EDIT_STAGED
        && editingEffectiveDate
        && toDateInputValue(editingEffectiveDate) !== toDateInputValue(effectiveDate);

    const addOverrideRow = () => setOverrides([...overrides, {day: undefined, fillPathId: undefined, pickPathId: undefined}]);
    const removeOverrideRow = (index) => setOverrides(overrides.filter((_, i) => i !== index));
    const updateOverride = (index, field, value) => {
        setOverrides(overrides.map((o, i) => (i === index ? {...o, [field]: value} : o)));
    };

    const onClickSave = () => {
        const errors = validateScheduleForm(currentForm(), mode, {stationNow, validPathIds});
        // ACTIVATE_NOW skips the generic "effective date required" check (the UI supplies the
        // station-now date), but if stationNow was blank/unparseable the date can be empty —
        // block the save so we never create an entry with no effective date.
        if (mode === SCHEDULE_MODAL_MODE.ACTIVATE_NOW && !effectiveDate) {
            errors.push({ id: "pathSchedule.validation.effectiveDateRequired",
                defaultMessage: "Please select an effective date" });
        }
        // Duplicate day-of-week override rows: overridesAsMap() keys by day, so two rows for
        // the same day would silently collapse (last wins). Detect it on the raw rows and
        // surface a clear error instead so the operator fixes the duplicate explicitly.
        const seenDays = new Set();
        const duplicateDays = new Set();
        overrides.forEach((o) => {
            if (o.day) {
                if (seenDays.has(o.day)) {
                    duplicateDays.add(o.day);
                }
                seenDays.add(o.day);
            }
        });
        duplicateDays.forEach((day) => {
            errors.push({ id: "pathSchedule.validation.overrideDuplicateDay",
                defaultMessage: "Only one override is allowed per day; remove the duplicate {day} override",
                values: { day } });
        });
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }
        if (conflictDetected && !conflictAcknowledged) {
            setConflictAcknowledged(true); // require a second confirm click after showing the warning
            return;
        }
        setValidationErrors([]);
        // Version is bound to the DDB key {stationCode, cycleId, effectiveDate}. Per the LLD
        // (Task 2, Version Handling), the version is only meaningful on a true same-key
        // overwrite — i.e., when the write targets the SAME effective date as the entry being
        // edited. For a write to a NEW date (ACTIVATE_NOW always; EDIT_STAGED when the operator
        // changed the date; CREATE), the entry at that date doesn't exist, so we must send no
        // version (treated as a new insert). Passing the source entry's version onto a new key
        // would either spuriously fail the optimistic-lock check or assert the wrong version.
        // Per the HLD, the original entry is intentionally left in place for the operator to
        // delete via the SOP (no automatic Set+Delete).
        const isSameKeyOverwrite = editingEffectiveDate
            && toDateInputValue(editingEffectiveDate) === toDateInputValue(effectiveDate);
        onSave({
            mode,
            effectiveDate,
            fillPathId,
            pickPathId,
            dayOfWeekOverrides: overridesAsMap(),
            version: isSameKeyOverwrite ? get(initialForm, ["version"]) : undefined
        });
    };

    const titleId = {
        [SCHEDULE_MODAL_MODE.CREATE]: "pathSchedule.modal.titleCreate",
        [SCHEDULE_MODAL_MODE.EDIT_ACTIVE]: "pathSchedule.modal.titleEditActive",
        [SCHEDULE_MODAL_MODE.EDIT_STAGED]: "pathSchedule.modal.titleEditStaged",
        [SCHEDULE_MODAL_MODE.ACTIVATE_NOW]: "pathSchedule.modal.titleActivateNow"
    }[mode] || "pathSchedule.modal.titleCreate";
    const titleDefault = {
        [SCHEDULE_MODAL_MODE.CREATE]: "Add New Schedule",
        [SCHEDULE_MODAL_MODE.EDIT_ACTIVE]: "Edit Active Schedule",
        [SCHEDULE_MODAL_MODE.EDIT_STAGED]: "Edit Schedule",
        [SCHEDULE_MODAL_MODE.ACTIVATE_NOW]: "Activate Now"
    }[mode] || "Add New Schedule";

    const renderPathSelect = (value, onChange, options, placeholderId, placeholderDefault) => (
        <Select value={value} onChange={onChange} size="small" width="100%"
                placeholder={intl.formatMessage({id: placeholderId, defaultMessage: placeholderDefault})}>
            {options.map((info) => {
                const id = get(info, ["sortPathId"]);
                const label = get(info, ["name"], id)
                    + " (" + get(info, ["source"], SORT_PATH_SOURCE.SPG) + ")";
                return <SelectOption key={id} value={id} label={label}/>;
            })}
        </Select>
    );

    return (
        <Modal title={intl.formatMessage({id: titleId, defaultMessage: titleDefault})}
               open={open} onClose={onClose} width="640px" bodySpacingInset="large"
               describedById="path-schedule-modal-description">
            <Column spacing="medium" id="path-schedule-modal-description">
                {saveError &&
                    <Alert type="error" size="large">{saveError.toString()}</Alert>
                }
                {validationErrors.length > 0 &&
                    <Alert type="error" size="large">
                        <Column spacing="none">
                            {validationErrors.map((e, i) => (
                                <Text key={i} type="b200">
                                    <FormattedMessage id={e.id} defaultMessage={e.defaultMessage} values={e.values}/>
                                </Text>
                            ))}
                        </Column>
                    </Alert>
                }
                {conflictDetected &&
                    <Alert type="warning" size="large">
                        <FormattedMessage id="pathSchedule.modal.conflictWarning"
                            defaultMessage="A schedule already exists for this date. Saving will overwrite it. Click Save again to continue."/>
                    </Alert>
                }
                {mode === SCHEDULE_MODAL_MODE.ACTIVATE_NOW &&
                    <Alert type="informational" size="large">
                        <FormattedMessage id="pathSchedule.modal.activateNowNote"
                            defaultMessage="This activates the configuration now using the station's local date. The original staged entry is not deleted and will reactivate on its future date — delete it from the list after verifying."/>
                    </Alert>
                }
                {isRescheduledStaged &&
                    <Alert type="informational" size="large">
                        <FormattedMessage id="pathSchedule.modal.rescheduleNote"
                            defaultMessage="Changing the effective date creates a new scheduled entry at the new date; the original entry is not moved or deleted. After saving, delete the original entry from the list so it does not reactivate on its old date."/>
                    </Alert>
                }

                <Row widths={["30%", "70%"]} alignmentVertical="center">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="pathSchedule.modal.cycle" defaultMessage="Cycle (CPT)"/>
                    </Text>
                    <Text type="b400">{cycleId}</Text>
                </Row>

                <Row widths={["30%", "70%"]} alignmentVertical="center">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="pathSchedule.modal.effectiveDate" defaultMessage="Effective Date"/>
                    </Text>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                    <div onClick={(e) => {
                        if (effectiveDateReadOnly) {
                            return;
                        }
                        // Meridian doesn't expose the underlying input ref, so reach for the
                        // native <input> and open the browser date picker on any click in the
                        // field (not just the calendar icon). Typing into the segments still works.
                        const input = e.currentTarget.querySelector("input");
                        if (input && typeof input.showPicker === "function") {
                            try {
                                input.showPicker();
                            } catch (err) {
                                // showPicker throws if not user-activated or unsupported; ignore.
                            }
                        }
                    }}>
                        <Input type="date" value={toDateInputValue(effectiveDate)} size="small" width={200}
                               disabled={effectiveDateReadOnly}
                               onChange={(v) => {
                                   setEffectiveDate(v || undefined);
                                   // Re-require conflict confirmation if the user moves to a
                                   // different (possibly also-occupied) date after acknowledging one.
                                   setConflictAcknowledged(false);
                               }}/>
                    </div>
                </Row>

                <Row widths={["30%", "70%"]} alignmentVertical="center">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="pathSchedule.modal.config" defaultMessage="Configuration (optional)"/>
                    </Text>
                    <Select value={selectedSettingId} onChange={onSelectConfig} size="small" width="100%"
                            placeholder={intl.formatMessage({id: "pathSchedule.modal.selectConfig",
                                defaultMessage: "Select a named configuration"})}>
                        {(Array.isArray(sortPathSettings) ? sortPathSettings : [])
                            .filter((s) => get(s, ["cycle"]) === cycleId)
                            .map((s) => {
                                const settingId = get(s, ["settingId"]);
                                return <SelectOption key={settingId} value={settingId} label={get(s, ["name"], settingId)}/>;
                            })}
                    </Select>
                </Row>

                <Row widths={["30%", "70%"]} alignmentVertical="center">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="pathSchedule.modal.fillPath" defaultMessage="Fill Path"/>
                    </Text>
                    {renderPathSelect(fillPathId, setFillPathId, fillOptions,
                        "pathSchedule.modal.selectFill", "Select Fill Path")}
                </Row>

                <Row widths={["30%", "70%"]} alignmentVertical="center">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="pathSchedule.modal.pickPath" defaultMessage="Pick Path"/>
                    </Text>
                    {renderPathSelect(pickPathId, setPickPathId, pickOptions,
                        "pathSchedule.modal.selectPick", "Select Pick Path")}
                </Row>

                <Column spacing="xsmall">
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Text type="h100">
                            <FormattedMessage id="pathSchedule.modal.overrides" defaultMessage="Day-of-Week Overrides"/>
                        </Text>
                        <Button type="link" size="small" onClick={addOverrideRow}>
                            <Icon tokens={plusTokens}/>
                            <FormattedMessage id="pathSchedule.modal.addOverride" defaultMessage="Add override"/>
                        </Button>
                    </Row>
                    {overrides.map((o, index) => (
                        <Row key={index} spacing="small" alignmentVertical="center" widths={["25%", "32%", "32%", "fill"]}>
                            <Select value={o.day} onChange={(v) => updateOverride(index, "day", v)} size="small"
                                    placeholder={intl.formatMessage({id: "pathSchedule.modal.day", defaultMessage: "Day"})}>
                                {DAYS_OF_WEEK.map((d) => <SelectOption key={d} value={d} label={d}/>)}
                            </Select>
                            {renderPathSelect(o.fillPathId, (v) => updateOverride(index, "fillPathId", v),
                                fillOptions, "pathSchedule.modal.selectFill", "Fill Path")}
                            {renderPathSelect(o.pickPathId, (v) => updateOverride(index, "pickPathId", v),
                                pickOptions, "pathSchedule.modal.selectPick", "Pick Path")}
                            <Button type="icon" size="small" onClick={() => removeOverrideRow(index)}>
                                <Icon tokens={trashTokens}>Remove</Icon>
                            </Button>
                        </Row>
                    ))}
                </Column>
            </Column>

            <ModalFooter>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="small" onClick={onClose} disabled={isSaving}>
                        <FormattedMessage id="pathSchedule.modal.cancel" defaultMessage="Cancel"/>
                    </Button>
                    <Button type="primary" size="small" onClick={onClickSave} disabled={isSaving}>
                        <FormattedMessage id="pathSchedule.modal.save" defaultMessage="Save"/>
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

export default injectIntl(AddScheduleModal);
